import { Check, Users, Sparkles, Flame, Layers, UserPlus, Zap } from "lucide-react";

// -----------------------------------------------------------------------------
// Data
// -----------------------------------------------------------------------------

const MONTHLY = [
  {
    key: "starter",
    name: "Starter",
    tag: "Entry Membership",
    icon: Zap,
    price: 79,
    unit: "/mo",
    description:
      "Build the habit. Show up. Get better every week — perfect for new athletes.",
    features: [
      "4 group sessions per month",
      "Consistent weekly training",
      "Save vs pay-per-session",
    ],
    highlight: false,
  },
  {
    key: "grinder",
    name: "The Grinder",
    tag: "Most Popular",
    icon: Flame,
    price: 149,
    unit: "/mo",
    description:
      "For athletes who train with intent. Group reps plus tighter small-group work.",
    features: [
      "4 group sessions per month",
      "4 small group sessions per month",
      "Position-specific work",
      "Personalized feedback",
    ],
    highlight: true,
  },
  {
    key: "blueprint-elite",
    name: "Blueprint Elite",
    tag: "Premium Membership",
    icon: Sparkles,
    price: 249,
    unit: "/mo",
    description:
      "The full stack — unlimited group reps, small-group tuning, and 1-on-1 coaching.",
    features: [
      "Unlimited group sessions",
      "4 small group sessions per month",
      "2 free 1-on-1 sessions per month",
      "Priority scheduling",
    ],
    highlight: false,
  },
];

const PACKS = [
  {
    key: "pack-5",
    name: "5 Session Pack",
    icon: Layers,
    price: 115,
    was: 125,
    description: "5 group sessions, paid up front.",
    features: ["5 group sessions", "No expiration cliff", "Save $10"],
  },
  {
    key: "pack-10",
    name: "10 Session Pack",
    icon: Layers,
    price: 275,
    was: 300,
    description: "10 group sessions for the athlete going all-in.",
    features: ["10 group sessions", "Best per-session value", "Save $25"],
    highlight: true,
  },
  {
    key: "blueprint-bundle",
    name: "The Blueprint Bundle",
    icon: Sparkles,
    price: 349,
    description: "Blended package — group reps and personal coaching.",
    features: ["5 group sessions", "2 one-on-one sessions", "90-day window"],
  },
];

const FRIEND = {
  key: "bring-a-friend",
  name: "Bring A Friend",
  price: 40,
  was: 50,
  description:
    "1 small group session for 2 athletes. Any day. First-timer friendly.",
  features: ["1 small group session", "2 players", "Any day, any slot"],
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const prefillContact = (tier) => {
  const price = tier.was
    ? `$${tier.price} (was $${tier.was})`
    : `$${tier.price}${tier.unit || ""}`;
  const message = `Hi — I'd like to sign up for the ${tier.name} option (${price}). Please let me know the next steps.`;
  window.dispatchEvent(
    new CustomEvent("bh:prefill-contact", { detail: { message, subject: tier.name } })
  );
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export const Memberships = () => {
  return (
    <section
      id="memberships"
      data-testid="memberships-section"
      className="bg-[#050505] py-20 md:py-32 border-b border-white/5 relative overflow-hidden"
    >
      <div className="absolute inset-0 bh-grid-bg opacity-20" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
            [ Pricing & Memberships ]
          </div>
          <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight">
            Pick Your <span className="text-[#2563EB]">Path</span>
          </h2>
          <p className="mt-6 text-[#A1A1AA] text-base md:text-lg leading-relaxed">
            Monthly memberships, session packs, or walk-up sessions. All
            training is 1 hour, Mon–Fri, 12 PM – 5 PM. Pay with Venmo or Zelle.
          </p>
          <div className="mt-6 inline-flex items-center gap-4 flex-wrap justify-center border border-white/5 bg-white/[0.02] px-4 py-2">
            <span className="text-[10px] tracking-[0.3em] uppercase text-white/50">
              Walk-Up Rates
            </span>
            <span className="text-white/80 text-sm">Large Group $25 · Small Group $35 · 1-on-1 $45</span>
          </div>
        </div>

        {/* Monthly Memberships */}
        <div className="mt-16">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
                [ 01 — Monthly Memberships ]
              </div>
              <h3 className="font-display mt-2 text-2xl md:text-3xl font-bold uppercase tracking-tight">
                Train Consistently
              </h3>
            </div>
            <p className="text-[#A1A1AA] text-sm max-w-md">
              Best value if you train every week. Cancel any month.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {MONTHLY.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.key}
                  data-testid={`membership-${t.key}`}
                  className={`relative p-8 md:p-10 flex flex-col ${
                    t.highlight ? "bg-[#0a1020] border-t-2 border-[#2563EB]" : "bg-[#070B14]"
                  }`}
                >
                  {t.highlight ? (
                    <div className="absolute top-0 right-0 bg-[#2563EB] text-white text-[10px] font-bold tracking-[0.3em] uppercase px-3 py-1.5">
                      {t.tag}
                    </div>
                  ) : (
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">
                      {t.tag}
                    </div>
                  )}

                  <div className="mt-6 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center border border-white/10 text-[#60A5FA]">
                      <Icon size={18} strokeWidth={1.6} />
                    </div>
                    <h4 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
                      {t.name}
                    </h4>
                  </div>
                  <div className="mt-5 flex items-baseline gap-2">
                    <span className="font-display text-5xl font-bold text-[#60A5FA]">
                      ${t.price}
                    </span>
                    <span className="text-sm tracking-widest uppercase text-white/50">
                      {t.unit}
                    </span>
                  </div>
                  <p className="mt-4 text-[#A1A1AA] text-sm leading-relaxed">
                    {t.description}
                  </p>

                  <ul className="mt-8 space-y-3 flex-1">
                    {t.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-3 text-sm text-white/85"
                      >
                        <Check size={16} className="text-[#2563EB] mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => prefillContact(t)}
                    data-testid={`membership-signup-${t.key}`}
                    className={`mt-8 inline-flex items-center justify-center w-full font-bold tracking-widest uppercase text-sm px-6 py-4 transition-colors rounded-sm ${
                      t.highlight
                        ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                        : "border border-white/20 hover:border-[#2563EB] hover:text-[#60A5FA] text-white"
                    }`}
                  >
                    Sign Up
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stack & Save (Session Packs) */}
        <div className="mt-20">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
                [ 02 — Stack & Save ]
              </div>
              <h3 className="font-display mt-2 text-2xl md:text-3xl font-bold uppercase tracking-tight">
                Session Packs
              </h3>
            </div>
            <p className="text-[#A1A1AA] text-sm max-w-md">
              Pay up front, save per session. No monthly commitment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {PACKS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.key}
                  data-testid={`pack-${p.key}`}
                  className={`relative p-8 md:p-10 flex flex-col ${
                    p.highlight ? "bg-[#0a1020] border-t-2 border-[#2563EB]" : "bg-[#070B14]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 flex items-center justify-center border border-white/10 text-[#60A5FA]">
                      <Icon size={18} strokeWidth={1.6} />
                    </div>
                    {p.was && (
                      <span className="text-[10px] tracking-[0.25em] uppercase text-[#60A5FA] border border-[#60A5FA]/30 px-2 py-1">
                        Save ${p.was - p.price}
                      </span>
                    )}
                  </div>

                  <h4 className="font-display mt-5 text-xl md:text-2xl font-bold uppercase tracking-tight">
                    {p.name}
                  </h4>

                  <div className="mt-4 flex items-baseline gap-3">
                    <span className="font-display text-4xl font-bold text-[#60A5FA]">
                      ${p.price}
                    </span>
                    {p.was && (
                      <span className="text-lg text-white/40 line-through">
                        ${p.was}
                      </span>
                    )}
                  </div>
                  <p className="mt-4 text-[#A1A1AA] text-sm leading-relaxed">
                    {p.description}
                  </p>

                  <ul className="mt-6 space-y-2.5 flex-1">
                    {p.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-white/85"
                      >
                        <Check size={14} className="text-[#2563EB] mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => prefillContact(p)}
                    data-testid={`pack-signup-${p.key}`}
                    className={`mt-8 inline-flex items-center justify-center w-full font-bold tracking-widest uppercase text-xs px-6 py-3.5 transition-colors rounded-sm ${
                      p.highlight
                        ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                        : "border border-white/20 hover:border-[#2563EB] hover:text-[#60A5FA] text-white"
                    }`}
                  >
                    Get This Pack
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bring a Friend */}
        <div className="mt-20">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA] mb-4">
            [ 03 — Bring a Friend ]
          </div>
          <div
            data-testid="bring-a-friend"
            className="bg-[#070B14] border border-white/5 p-8 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-8 items-center"
          >
            <div className="md:col-span-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center border border-white/10 text-[#60A5FA]">
                  <UserPlus size={18} strokeWidth={1.6} />
                </div>
                <h4 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
                  {FRIEND.name}
                </h4>
              </div>
              <p className="mt-4 text-[#A1A1AA] text-base leading-relaxed max-w-2xl">
                {FRIEND.description}
              </p>
              <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                {FRIEND.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-2 text-sm text-white/85"
                  >
                    <Check size={14} className="text-[#2563EB]" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col items-start md:items-end">
              <div className="flex items-baseline gap-3">
                <span className="font-display text-5xl font-bold text-[#60A5FA]">
                  ${FRIEND.price}
                </span>
                <span className="text-lg text-white/40 line-through">
                  ${FRIEND.was}
                </span>
              </div>
              <span className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#60A5FA]">
                Save ${FRIEND.was - FRIEND.price}
              </span>
              <button
                onClick={() => prefillContact(FRIEND)}
                data-testid="friend-signup"
                className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold tracking-widest uppercase text-sm px-8 py-4 transition-colors rounded-sm"
              >
                <Users size={16} /> Bring A Friend
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Memberships;
